import { LinkOutlined } from '@ant-design/icons';
import { theme } from 'antd';
import { CREDENTIAL_TYPE_PASSCODE_CARD } from 'config/credentialTypes';
import { useManagePermittedRoles } from 'library/hooks';
import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { credentialsActions } from 'reduxStore/credentials/slice';
import { selectCredentialByID, selectCredentialLoading } from 'reduxStore/credentials/selectors';
import { Button, Flex, Form, Input, Modal, Tooltip, Typography } from 'zui/Antd';

const { Title, Text } = Typography;

const AssociateCardModal = ({ credentialID, children }) => {
	const canManageCredential = useManagePermittedRoles('updateCredential');
	const {
		token: { paddingXL, fontSizeSM, marginLG, colorTextDescription, fontWeightStrong, colorBorderSecondary, paddingSM }
	} = theme.useToken();

	const dispatch = useDispatch();
	const [form] = Form.useForm();
	const [visible, setVisible] = useState(false);

	const credential = useSelector((state) => selectCredentialByID(state, credentialID));
	const isCredentialLoading = !!useSelector((state) => selectCredentialLoading(state, credentialID));

	const isCard = credential?.credentialTypeID === CREDENTIAL_TYPE_PASSCODE_CARD;
	const isTriggerDisabled = !canManageCredential || !isCard;
	const triggerTitle = !canManageCredential
		? "You don't have permission to perform this action."
		: !isCard
			? 'Only card credentials can be associated.'
			: 'Associate Card';

	const handleCancel = () => {
		form.resetFields();
		setVisible(false);
	};

	const handleFinish = (values) => {
		dispatch(credentialsActions.associateCard({
			credentialID,
			data: { uniqueIdentifier: values.uniqueIdentifier }
		}));
		form.resetFields();
	};

	return (
		<>
			<Tooltip title={triggerTitle}>
				<span>
					{children ? (
						<span onClick={() => !isTriggerDisabled && setVisible(true)}>{children}</span>
					) : (
						<Button
							type='text'
							size='small'
							icon={<LinkOutlined />}
							onClick={(e) => {
								e.stopPropagation();
								!isTriggerDisabled && setVisible(true);
							}}
							disabled={isTriggerDisabled}
						/>
					)}
				</span>
			</Tooltip>
			<Modal
				open={visible}
				onCancel={handleCancel}
				width={500}
				centered
				closeIcon={false}
				destroyOnClose
				styles={{
					content: { padding: 0 },
					body: { padding: paddingXL },
					footer: {
						padding: paddingXL,
						borderTop: `1px solid ${colorBorderSecondary}`
					}
				}}
				footer={[
					<Flex gap={paddingSM} align='center' justify='flex-end' key='footer'>
						<Button key='cancel' onClick={handleCancel} disabled={isCredentialLoading}>
							Cancel
						</Button>
						<Button
							type='primary'
							key='submit'
							loading={isCredentialLoading}
							onClick={() => form.submit()}
						>
							Associate
						</Button>
					</Flex>
				]}
			>
				<Title level={4} style={{ margin: 0 }}>
					Associate Card
				</Title>
				<Text style={{ lineHeight: '20px', fontSize: fontSizeSM, color: colorTextDescription }}>
					Enter or scan the CSN (Card Serial Number) to link this card.
				</Text>

				<Form form={form} layout='vertical' onFinish={handleFinish} style={{ marginTop: marginLG }}>
					<Form.Item
						name='uniqueIdentifier'
						label='CSN (Card Serial Number)'
						rules={[{ required: true, message: 'Please enter a CSN' }]}
						required={false}
						style={{ fontWeight: fontWeightStrong, marginBottom: 42 }}
					>
						<Input
							placeholder='Enter or scan CSN...'
							style={{ fontWeight: 400 }}
							onKeyDown={(e) => {
								if (e.key === 'Enter') e.preventDefault();
							}}
						/>
					</Form.Item>
				</Form>
			</Modal>
		</>
	);
};

export default AssociateCardModal;
